/**
 * Dialogs, menus and snackbars: the parts that float above the page.
 *
 * Accessibility is the point of writing them by hand rather than skipping
 * them: a dialog traps focus and gives it back, Escape closes what it can,
 * a menu moves with the arrow keys and every entry is a real link when it
 * navigates, so "open in a new tab" works from a menu too.
 */
import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { Icon, IconButton, cx } from './primitives.js'
import type { IconName } from './icons.js'
import { useI18n } from '../i18n/context.js'

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((element) => element.offsetParent !== null || element === document.activeElement)
}

export function Dialog({ title, onClose, children, actions, size = 'sm', icon, dismissible = true, description, className }: {
  title: ReactNode
  onClose: () => void
  children?: ReactNode
  actions?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  icon?: IconName
  dismissible?: boolean
  description?: ReactNode
  className?: string
}) {
  const { t } = useI18n()
  const panel = useRef<HTMLDivElement | null>(null)
  const titleId = useId()
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    const root = panel.current
    if (root) {
      const preferred = root.querySelector<HTMLElement>('[data-autofocus], [autofocus]') ?? focusables(root).find((element) => !element.classList.contains('dialog-close')) ?? root
      preferred.focus()
    }
    document.body.classList.add('has-dialog')
    return () => {
      document.body.classList.remove('has-dialog')
      previous?.focus?.()
    }
  }, [])

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && dismissible) {
      event.stopPropagation()
      onCloseRef.current()
      return
    }
    if (event.key !== 'Tab' || !panel.current) return
    const items = focusables(panel.current)
    const first = items[0]
    const last = items[items.length - 1]
    if (!first || !last) return
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
  }

  return createPortal(
    <div className="dialog-scrim" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && dismissible) onClose() }}>
      <div ref={panel} className={cx('dialog', `dialog-${size}`, className)} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={onKeyDown}>
        <header className="dialog-head">
          {icon && <Icon name={icon} className="dialog-icon"/>}
          <h2 id={titleId} className="dialog-title">{title}</h2>
          {dismissible && <IconButton icon="close" label={t('common.close')} className="dialog-close" onClick={onClose}/>}
        </header>
        {description && <div className="dialog-description">{description}</div>}
        {children && <div className="dialog-body">{children}</div>}
        {actions && <footer className="dialog-actions">{actions}</footer>}
      </div>
    </div>,
    document.body,
  )
}

export interface MenuEntry {
  key: string
  label: string
  icon?: IconName
  /** An in-app destination: rendered as a real link. */
  to?: string
  /** An external destination: new tab. */
  href?: string
  onSelect?: () => void
  danger?: boolean
  disabled?: boolean
  /** A line above this entry. */
  divider?: boolean
  secondary?: string
}

/**
 * A button that opens a list of actions. Positioned `fixed` from the trigger,
 * so a menu inside a scrolling table is never clipped by it.
 */
export function MenuButton({ items, label, icon = 'more_vert', trigger, align = 'end', header, className }: {
  items: MenuEntry[]
  label: string
  icon?: IconName
  trigger?: (props: { onClick: () => void; 'aria-expanded': boolean; 'aria-haspopup': 'menu'; 'aria-controls': string; ref: (element: HTMLButtonElement | null) => void }) => ReactNode
  align?: 'start' | 'end'
  header?: ReactNode
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left?: number; right?: number; maxHeight: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const id = useId()

  const close = useCallback((restoreFocus = true) => {
    setOpen(false)
    if (restoreFocus) triggerRef.current?.focus()
  }, [])

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const below = window.innerHeight - rect.bottom - 8
    const above = rect.top - 8
    const openUp = below < 240 && above > below
    const maxHeight = Math.max(160, (openUp ? above : below) - 4)
    const top = openUp ? Math.max(8, rect.top - Math.min(maxHeight, menuRef.current?.offsetHeight ?? maxHeight) - 4) : rect.bottom + 4
    setPosition(align === 'end'
      ? { top, right: Math.max(8, window.innerWidth - rect.right), maxHeight }
      : { top, left: Math.max(8, rect.left), maxHeight })
  }, [open, align])

  useEffect(() => {
    if (!open) return
    const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')
    first?.focus()
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node
      if (!menuRef.current?.contains(target) && !triggerRef.current?.contains(target)) close(false)
    }
    const onScroll = (event: Event) => {
      if (menuRef.current && event.target instanceof Node && menuRef.current.contains(event.target)) return
      close(false)
    }
    document.addEventListener('pointerdown', onPointer)
    window.addEventListener('resize', onScroll)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      window.removeEventListener('resize', onScroll)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open, close])

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const entries = [...(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? [])]
    const index = entries.indexOf(document.activeElement as HTMLElement)
    if (event.key === 'Escape') { event.preventDefault(); close() }
    else if (event.key === 'Tab') close(false)
    else if (event.key === 'ArrowDown') { event.preventDefault(); entries[(index + 1) % entries.length]?.focus() }
    else if (event.key === 'ArrowUp') { event.preventDefault(); entries[(index - 1 + entries.length) % entries.length]?.focus() }
    else if (event.key === 'Home') { event.preventDefault(); entries[0]?.focus() }
    else if (event.key === 'End') { event.preventDefault(); entries[entries.length - 1]?.focus() }
  }

  const toggle = () => setOpen((current) => !current)
  const setTrigger = (element: HTMLButtonElement | null) => { triggerRef.current = element }

  return <span className={cx('menu-anchor', className)}>
    {trigger
      ? trigger({ onClick: toggle, 'aria-expanded': open, 'aria-haspopup': 'menu', 'aria-controls': id, ref: setTrigger })
      : <button ref={setTrigger} type="button" className="icon-btn" aria-label={label} title={label} aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle}><Icon name={icon}/></button>}
    {open && createPortal(
      <div ref={menuRef} id={id} className="menu-surface" role="menu" aria-label={label} onKeyDown={onKeyDown}
        style={position ? { top: position.top, left: position.left, right: position.right, maxHeight: position.maxHeight } : { visibility: 'hidden' }}>
        {header && <div className="menu-header" role="presentation">{header}</div>}
        {items.map((item) => {
          const content = <>
            {item.icon && <Icon name={item.icon} size={20}/>}
            <span className="menu-item-text"><span>{item.label}</span>{item.secondary && <small>{item.secondary}</small>}</span>
            {item.href && <Icon name="open_in_new" size={18} className="menu-item-trailing"/>}
          </>
          const classes = cx('menu-item', item.danger && 'is-danger', item.divider && 'has-divider')
          if (item.disabled) return <span key={item.key} className={cx(classes, 'is-disabled')} role="menuitem" aria-disabled="true" tabIndex={-1}>{content}</span>
          if (item.to) return <Link key={item.key} to={item.to} className={classes} role="menuitem" tabIndex={-1} onClick={() => close(false)}>{content}</Link>
          if (item.href) return <a key={item.key} href={item.href} target="_blank" rel="noopener noreferrer" className={classes} role="menuitem" tabIndex={-1} onClick={() => close(false)}>{content}</a>
          return <button key={item.key} type="button" className={classes} role="menuitem" tabIndex={-1} onClick={() => { close(); item.onSelect?.() }}>{content}</button>
        })}
      </div>,
      document.body,
    )}
  </span>
}

export interface TabEntry {
  key: string
  label: ReactNode
  to: string
  icon?: IconName
  badge?: ReactNode
}

/** Tabs whose selection is in the URL: each tab is a link. */
export function TabLinks({ items, active, label }: { items: TabEntry[]; active: string; label: string }) {
  return <nav className="tabs" aria-label={label}>
    {items.map((item) => <Link key={item.key} to={item.to} replace className={cx('tab', item.key === active && 'is-active')} aria-current={item.key === active ? 'page' : undefined}>
      {item.icon && <Icon name={item.icon} size={20}/>}
      <span>{item.label}</span>
      {item.badge !== undefined && <span className="tab-badge">{item.badge}</span>}
    </Link>)}
  </nav>
}

interface SnackbarMessage {
  id: number
  text: string
  tone: 'default' | 'error'
  action?: { label: string; onClick: () => void }
}

type ShowSnackbar = (text: string, options?: { tone?: 'default' | 'error'; action?: { label: string; onClick: () => void } }) => void

const SnackbarContext = createContext<ShowSnackbar | null>(null)

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n()
  const [message, setMessage] = useState<SnackbarMessage | null>(null)
  const counter = useRef(0)

  const show = useCallback<ShowSnackbar>((text, options) => {
    counter.current += 1
    setMessage({ id: counter.current, text, tone: options?.tone ?? 'default', action: options?.action })
  }, [])

  useEffect(() => {
    if (!message) return
    const timer = window.setTimeout(() => setMessage(null), message.tone === 'error' ? 9000 : 5000)
    return () => window.clearTimeout(timer)
  }, [message])

  const value = useMemo(() => show, [show])
  return <SnackbarContext.Provider value={value}>
    {children}
    <div className="snackbar-region" role="status" aria-live="polite">
      {message && <div key={message.id} className={cx('snackbar', message.tone === 'error' && 'snackbar-error')}>
        <span className="snackbar-text">{message.text}</span>
        {message.action && <button type="button" className="snackbar-action" onClick={() => { message.action?.onClick(); setMessage(null) }}>{message.action.label}</button>}
        <IconButton icon="close" label={t('common.close')} className="snackbar-close" onClick={() => setMessage(null)}/>
      </div>}
    </div>
  </SnackbarContext.Provider>
}

export function useSnackbar(): ShowSnackbar {
  const value = useContext(SnackbarContext)
  if (!value) throw new Error('useSnackbar must be used inside SnackbarProvider.')
  return value
}
